import type React from 'react';

import type { PlainDate } from '../../core/services/plain-date';

/** Inclusive range of epoch days. `startDay === endDay` is a one-day span. */
export type Span = { startDay: number; endDay: number };

export type GanttItem = {
  id: string;
  name: string;
  /** `milestone` rendering lands with the dependency work; phase (a) renders every item as a bar. */
  kind?: 'bar' | 'milestone';
  startDate?: PlainDate;
  dueDate?: PlainDate;
  isDone?: boolean;
  /** Default `true`. `false` removes drag and resize affordances for this item only. */
  editable?: boolean;
  children?: GanttItem[];
  /** Opaque consumer payload, handed back untouched to the renderers. */
  data?: unknown;
};

export type Row = {
  id: string;
  item: GanttItem;
  parentId?: string;
  /** 0 for a top-level item. */
  depth: number;
  /** Position in the visible row array — the row's vertical index. */
  index: number;
  isGroup: boolean;
  isCollapsed: boolean;
  /** Absent when the item and all its descendants are undated. */
  span?: Span;
};

export type Zoom = 'day' | 'week' | 'month' | 'quarter';

export type Tick = {
  /** First epoch day covered by this tick. */
  day: number;
  /**
   * Pixels from the shared coordinate origin — the same `windowStartDay` passed to `xOf` and
   * `barLayout` — to the left edge of this tick. Always `xOf(day, windowStartDay)`, so a tick and
   * a bar computed from the same origin sit in the same coordinate system.
   *
   * Legitimately negative: at `week`/`month`/`quarter` zoom the first segment can begin before
   * `fromDay` (e.g. a `week`-zoom window starting mid-week begins its first tick on the preceding
   * Monday), so `x` for that tick is negative even though `fromDay` and `windowStartDay` are the
   * window's own start.
   *
   * Pixel domain — prefer this for anything comparing directly against a scroll position
   * (`scrollLeft`) or another pixel value. For a value that survives a zoom change without
   * recomputation, use `day` and `days` instead: multiply by `--gantt-px-per-day` in CSS.
   */
  x: number;
  /** Pixel width of this tick's column. Pixel-domain counterpart of `days`; see `x`. */
  width: number;
  /** The segment's length in days — the day-domain counterpart of `width`. */
  days: number;
  /** Non-working day per `workingWeek` (film production shoots weekends, so this is
   * working-week-relative, not literally Saturday/Sunday). Only meaningful at `day` zoom; false
   * at every coarser zoom. */
  isNonWorking: boolean;
  isToday: boolean;
  /** Primary label: the day number at `day` zoom. */
  label: string;
  /** Secondary label: the weekday abbreviation at `day` zoom, absent otherwise. */
  subLabel?: string;
  /** The band above the ticks: "August 2026" at `day` zoom. */
  groupLabel: string;
};

export type TimeScale = {
  zoom: Zoom;
  pxPerDay: number;
  /** Pixels from `windowStartDay` to the left edge of `day`. */
  xOf: (day: number, windowStartDay: number) => number;
  /** Inverse of `xOf`, floored to the day whose column contains `x`. */
  dayAt: (x: number, windowStartDay: number) => number;
  /** A pixel delta from a drag, rounded to whole days. */
  snapDays: (dx: number) => number;
  /**
   * Generated on demand for the visible window — never materialized for the whole range.
   * `windowStartDay` is the same coordinate origin passed to `xOf`/`barLayout`, so `Tick.x` and a
   * bar's `x` for the same day always agree. Returns `[]` when `toDay < fromDay`.
   */
  ticks: (
    fromDay: number,
    toDay: number,
    windowStartDay: number,
    today: number,
    workingWeek: readonly number[],
  ) => Tick[];
};

export type GanttPatch = { id: string; startDate?: PlainDate; dueDate?: PlainDate };

export type BarState = {
  isDragging: boolean;
  isResizing: 'start' | 'end' | false;
  isFocused: boolean;
  /**
   * Present only mid-interaction. Renderers must draw this in preference to the item's dates.
   *
   * This guarantee is delivered by the DEFAULT `Bar`/`GroupBar` (`view/bar.tsx`,
   * `view/group-bar.tsx`) only — each owns its own `useBarDrag` call and derives this field from
   * it directly, because `row-list.tsx` cannot call a hook once per row inside its virtualized
   * `rows.map()` without varying the hook count across renders. A CUSTOM `renderers.bar` /
   * `renderers.groupBar` is handed this field from `row-list.tsx`'s static, always-idle baseline
   * `BarState`, so it is always `undefined` there — a custom renderer that wants this contract to
   * hold for real must call the exported `useBarDrag` itself, the same way the default bar does.
   */
  previewSpan?: Span;
};

/**
 * A component keyed on one row. Consumers pass components, never pre-built elements: an element
 * per item would have to be constructed up front, for collapsed and off-screen rows too, which
 * is exactly what removing `nameSuffix`/`headerSuffix`/`sideHeader` from `GanttItem` bought.
 */
export type RowSlot = React.ComponentType<{ row: Row }>;

/**
 * Everything `row-list.tsx` hands a bar renderer, default or custom — spec §6.2's seam, stated as
 * the contract it actually is.
 *
 * WHY THESE THREE FIELDS EXIST HERE. The type used to be `{ row: Row; state: BarState }`, from
 * Task 12, and it was never widened as Tasks 18, 20 and 22 added `scale`, `readOnly` and
 * `onCommit` to `Bar`/`GroupBar` AND to the call site. Each per-task diff looked complete; only
 * the union was wrong, and it produced two real `tsc` errors at `row-list.tsx`'s two render sites
 * that nothing surfaced — `nx typecheck webapp` dies on `@producer/ui-kit`'s 57 pre-existing
 * errors before webapp's own project is ever reached, and Vite does not typecheck on build.
 *
 * The semantic half matters more than the errors. `BarState.previewSpan`'s note below says a
 * custom renderer that wants live drag "must call the exported `useBarDrag` itself" — but
 * `useBarDrag` requires a `TimeScale`, and the old declaration gave a custom renderer no way to
 * obtain one. The renderer seam was contractually unusable for anything interactive, which is half
 * of what this rebuild exists to deliver.
 *
 * Every field past `row`/`state` is OPTIONAL, and that is a deliberate contract choice rather than
 * laxity: it is what keeps a minimal `({ row, state }) => …` renderer assignable, and it is also
 * what `Bar`'s own signature declares (so a direct `<Bar row state />` render still works without
 * a live chart around it — see `BarProps`).
 */
export type BarRendererProps = {
  row: Row;
  state: BarState;
  /** The chart's shared day<->pixel scale, so a custom renderer can position itself and can feed
   * `useBarDrag`. Absent only on a direct render with no chart around it. */
  scale?: TimeScale;
  /** `GanttConfig.readOnly`. Fold it into `isDragDisabledFor` rather than testing it alone — that
   * one function is what keeps `Bar` and `GroupBar` from drifting on who may edit a row. */
  readOnly?: boolean;
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
};

/** `BarRendererProps` plus the group bar's own slot. */
export type GroupBarRendererProps = BarRendererProps & {
  /** `renderers.groupBarSuffix`, forwarded so a replacement group bar can still place the badge. */
  suffix?: RowSlot;
};

export type GanttRenderers = {
  rowHeader?: RowSlot;
  bar?: React.ComponentType<BarRendererProps>;
  /** Receives `groupBarSuffix` too, so a replacement group bar can still place the badge. */
  groupBar?: React.ComponentType<GroupBarRendererProps>;
  /**
   * Drawn inside the group bar, ahead of the name. This is the stage-number badge both routes
   * show today — the old `GanttItem.nameSuffix` that `ParentStrip` rendered — which §3.1's
   * removal table routed to `rowHeader` alone, leaving §7's group-bar suffix unbuildable.
   */
  groupBarSuffix?: RowSlot;
};

export type GanttConfig = {
  /** Only `'day'` is selectable in phase (a). */
  zoom?: Zoom;
  collapsed?: ReadonlySet<string>;
  defaultCollapsed?: ReadonlySet<string>;
  onCollapsedChange?: (next: ReadonlySet<string>) => void;
  /** Days of the week that are working days, as `dayOfWeek` numbers. Default Mon-Fri. */
  workingWeek?: readonly number[];
  readOnly?: boolean;
  /** Injectable for tests; defaults to the real today at render time. */
  today?: PlainDate;
  range?: { padMonths?: number; extendMonths?: number; maxSpanYears?: number };
};

export const DEFAULT_WORKING_WEEK: readonly number[] = [1, 2, 3, 4, 5];
